import { Grow, Paper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

const MARGIN = 16;
const RESIZE_HANDLE_SIZE = 18;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const RESIZE_CORNERS = [
  { corner: 'nw', cursor: 'nwse-resize', pos: { left: 0, top: 0 } },
  { corner: 'ne', cursor: 'nesw-resize', pos: { right: 0, top: 0 } },
  { corner: 'sw', cursor: 'nesw-resize', pos: { left: 0, bottom: 0 } },
  { corner: 'se', cursor: 'nwse-resize', pos: { right: 0, bottom: 0 } },
];

/**
 * A floating Paper anchored bottom-right that can be dragged by its header and resized from any
 * corner. Its box is kept in sessionStorage under storageKey so it survives closing and reopening.
 * The first child is treated as the drag handle.
 */
export const FloatingPanel = ({
  open,
  storageKey,
  defaultWidth = 420,
  defaultHeight = 560,
  minWidth = 320,
  minHeight = 360,
  children,
}) => {
  const [box, setBox] = useState(() => {
    const stored = JSON.parse(sessionStorage.getItem(storageKey));
    if (stored) return stored;
    return {
      width: defaultWidth,
      height: defaultHeight,
      left: Math.max(MARGIN, window.innerWidth - defaultWidth - MARGIN),
      top: Math.max(MARGIN, window.innerHeight - defaultHeight - MARGIN),
    };
  });
  const gestureRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(box));
  }, [storageKey, box]);

  // Detach listeners if the panel unmounts mid-gesture
  useEffect(
    () => () => {
      const gesture = gestureRef.current;
      if (gesture) {
        window.removeEventListener('pointermove', gesture.handleMove);
        window.removeEventListener('pointerup', gesture.handleUp);
      }
    },
    [],
  );

  const beginGesture = (event, corner) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const start = box;

    const handleMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (!corner) {
        setBox({
          ...start,
          left: clamp(start.left + dx, 0, Math.max(0, window.innerWidth - start.width)),
          top: clamp(start.top + dy, 0, Math.max(0, window.innerHeight - start.height)),
        });
        return;
      }
      // Resize: the corner opposite the grabbed one stays anchored
      const right = start.left + start.width;
      const bottom = start.top + start.height;
      let { left, top, width, height } = start;
      if (corner === 'nw' || corner === 'sw') {
        left = clamp(start.left + dx, 0, right - minWidth);
        width = right - left;
      } else {
        width = clamp(start.width + dx, minWidth, window.innerWidth - start.left);
      }
      if (corner === 'nw' || corner === 'ne') {
        top = clamp(start.top + dy, 0, bottom - minHeight);
        height = bottom - top;
      } else {
        height = clamp(start.height + dy, minHeight, window.innerHeight - start.top);
      }
      setBox({ left, top, width, height });
    };
    const handleUp = () => {
      gestureRef.current = null;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    gestureRef.current = { handleMove, handleUp };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  const handleHeaderPointerDown = (event) => {
    // Buttons in the header work normally instead of starting a drag
    if (event.target.closest('button')) return;
    beginGesture(event);
  };

  const [header, ...body] = Array.isArray(children) ? children : [children];

  return (
    <Grow in={open} style={{ transformOrigin: 'bottom right' }} unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          zIndex: (theme) => theme.zIndex.modal,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          onPointerDown={handleHeaderPointerDown}
          style={{ cursor: 'move', userSelect: 'none', touchAction: 'none' }}
        >
          {header}
        </div>
        {body}
        {RESIZE_CORNERS.map(({ corner, cursor, pos }) => (
          <div
            key={corner}
            onPointerDown={(event) => beginGesture(event, corner)}
            style={{
              position: 'absolute',
              ...pos,
              width: RESIZE_HANDLE_SIZE,
              height: RESIZE_HANDLE_SIZE,
              cursor,
              touchAction: 'none',
              zIndex: 1,
            }}
          />
        ))}
      </Paper>
    </Grow>
  );
};
